import { useRef, useState, type ReactNode } from 'react';

import { cn } from '../lib/cn';
import { useFieldId } from '../lib/useId';

export interface TooltipProps {
  /** Short. A tooltip is a hint, not documentation. */
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
  /**
   * Where along that side. `start` lines the bubble up with the trigger's left edge,
   * for a trigger at the start of a clipped container (a table's first column), where a
   * centred bubble wider than the trigger would be cut off on the left.
   */
  align?: 'center' | 'start';
  delayMs?: number;
  className?: string;
}

const startPositions = {
  top: 'bottom-full left-0 mb-1.5',
  bottom: 'top-full left-0 mt-1.5',
};

const positions = {
  top: 'bottom-full left-1/2 -translate-x-1/2 mb-1.5',
  bottom: 'top-full left-1/2 -translate-x-1/2 mt-1.5',
  left: 'right-full top-1/2 -translate-y-1/2 mr-1.5',
  right: 'left-full top-1/2 -translate-y-1/2 ml-1.5',
};

/**
 * A hint on hover or focus.
 *
 * Two rules. It is shown on FOCUS as well as hover, so a keyboard user gets it at
 * all. And it is never the only place information lives — a tooltip is invisible on
 * touch, unreadable when zoomed, and gone by the time someone re-reads the sentence.
 * If a control needs an explanation to be operated, that explanation belongs in a
 * label or a hint, not here.
 */
export function Tooltip({
  content,
  children,
  side = 'top',
  align = 'center',
  delayMs = 250,
  className,
}: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const id = useFieldId();

  const show = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setVisible(true), delayMs);
  };
  const hide = () => {
    clearTimeout(timer.current);
    setVisible(false);
  };

  return (
    <span
      className={cn('relative inline-flex', className)}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocusCapture={show}
      onBlurCapture={hide}
      // Escape dismisses a tooltip: WCAG 1.4.13 content-on-hover.
      onKeyDown={(event) => event.key === 'Escape' && hide()}
      aria-describedby={visible ? id : undefined}
    >
      {children}
      {visible && (
        <span
          role="tooltip"
          id={id}
          className={cn(
            'pointer-events-none absolute z-50 w-max max-w-xs animate-fade-in rounded [overflow-wrap:anywhere]',
            'bg-ink px-2 py-1 text-xs font-medium text-ink-inverse shadow-lg',
            align === 'start' && (side === 'top' || side === 'bottom')
              ? startPositions[side]
              : positions[side],
          )}
        >
          {content}
        </span>
      )}
    </span>
  );
}
